import moment from "moment";

import {
  ParametersModelRun,
  StandardDataConstantDimension,
  StandardDataDimension,
  StandardDataEnumDimension,
  StandardDataTimeRangeDimension,
  SteppedTimeRange,
  type ParametersVerticalExtent,
} from "@/lib/api";

import { TimeRangeDimension, TimeRangeDimensionValue } from "./time-range-dimension";
import { EnumDimension, EnumDimensionValue } from "./enum-dimension";
import { ConstantDimension, ConstantDimensionValue } from "./constant-dimension";

import { Dimension, DimensionValueSet } from ".";

export function parseStandardDataDimensions(
  dimensionsData: StandardDataDimension[],
): Dimension[] {
  return dimensionsData
    .map((dimension) => {
      switch (dimension.type) {
        case "TimeRange":
          return TimeRangeDimension.fromData(dimension as StandardDataTimeRangeDimension);
        case "Enum":
          return EnumDimension.fromData(dimension as StandardDataEnumDimension);
        case "Constant":
          return ConstantDimension.fromData(dimension as StandardDataConstantDimension);
        default:
          console.warn("unknown dimension type", dimension);
          break;
      }
    })
    .filter((x) => !!x);
}

export function getRelevantDimensionValues(
  dimensions: Dimension[] | undefined,
  values: DimensionValueSet,
): DimensionValueSet {
  const relevantValues = {} as DimensionValueSet;
  dimensions?.forEach((dimension) => {
    const value = dimension.snap(values[dimension.id]) ?? dimension.default;
    if (value) {
      relevantValues[dimension.id] = value;
    }
  });
  return relevantValues;
}

export function dynamicDimensionsContainValues(
  dimensions: Dimension[],
  values: DimensionValueSet,
) {
  return dimensions
    .filter((it) => !(it instanceof ConstantDimension))
    .every((dimension) => dimension.contains(values[dimension.id]));
}

export function compareDimensionValueSets(
  a: DimensionValueSet,
  b: DimensionValueSet,
): boolean {
  if (Object.keys(a).length !== Object.keys(b).length) return false;

  return Object.entries(a).every(([dimensionId, dimensionValue]) => {
    return dimensionValue.queryStringValue === b[dimensionId]?.queryStringValue;
  });
}

export function getCacheKeyForDimensionValueSet(
  dimensionValues: DimensionValueSet,
): string {
  return JSON.stringify(
    Object.entries(dimensionValues).map(([id, value]) => [id, value.queryStringValue]),
  );
}

export function getDimensionsFromModelRun(modelRun: ParametersModelRun) {
  const dimensions: Dimension[] = [];

  dimensions.push(
    new ConstantDimension({
      id: "reference_time",
      value: new ConstantDimensionValue(modelRun.reference_time),
    }),
  );

  if (!modelRun) return dimensions;

  const refTime = new Date(modelRun.reference_time);
  const intervalInMilliseconds = moment.duration(modelRun.interval).asMilliseconds();
  const start = new TimeRangeDimensionValue(new Date(+refTime + intervalInMilliseconds));
  const end = new TimeRangeDimensionValue(
    new Date(+refTime + intervalInMilliseconds * (modelRun.length - 1)),
  );

  const values: TimeRangeDimensionValue[] = [];
  let current = start;
  while (current.timestamp <= end.timestamp) {
    values.push(current);
    current = new TimeRangeDimensionValue(
      new Date(+current.timestamp + intervalInMilliseconds),
    );
  }

  dimensions.push(
    new TimeRangeDimension({
      id: "time",
      start: values[0],
      end: values[values.length - 1],
      intervalInMilliseconds,
      values,
    }),
  );
  return dimensions;
}

export function getTimeDimensionFromTemporalExtent(
  temporalExtent: SteppedTimeRange | null,
) {
  if (!temporalExtent) return [];

  const start = new TimeRangeDimensionValue(new Date(temporalExtent.begin));
  const end = new TimeRangeDimensionValue(new Date(temporalExtent.end));
  const stepInMilliseconds = moment.duration(temporalExtent.step).asMilliseconds();

  const values: TimeRangeDimensionValue[] = [];
  let current = start;
  while (current.timestamp <= end.timestamp) {
    values.push(current);
    current = new TimeRangeDimensionValue(
      new Date(+current.timestamp + stepInMilliseconds),
    );
  }

  return [
    new TimeRangeDimension({
      id: "time",
      start: values[0],
      end: values[values.length - 1],
      intervalInMilliseconds: stepInMilliseconds,
      values,
    }),
  ];
}

/** Same id as the old map widget, so `map.dimension_names.*` renders "AHAGL". */
export const verticalDimensionId = "aircraft_height_above_ground_level_in_ft";

export function getVerticalDimensionFromExtent(
  extent?: ParametersVerticalExtent | null,
): EnumDimension | undefined {
  if (!extent || extent.values.length === 0) return undefined;

  const values = extent.values.map((value) => new EnumDimensionValue(String(value)));

  return new EnumDimension({
    id: verticalDimensionId,
    values,
    default: values[0],
    property: { id: extent.axis.id },
    unit: {
      type: extent.axis.unit.code,
      symbol: extent.axis.unit.symbol ?? "",
    },
  });
}

export function appendVerticalQueryParam(
  params: URLSearchParams,
  dimensionValues: DimensionValueSet,
): void {
  const value = dimensionValues[verticalDimensionId]?.queryStringValue;
  if (!value) return;

  params.set("vertical", value);
}
